<template>
    <modal-side-generic
    sizeClass="big-plus"
    :title="t('cards.cardlessWithdraw.generateCode.reuseModal.title')"
    :subTitle="t('cards.cardlessWithdraw.generateCode.reuseModal.subtitle')"
    :tabs="tabsList"
    @hideMe="emit('hideMe')">
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader" fullSize/>
            <table-generic
            v-if="!showLoader && getFavorites.length"
            :headerData="tableHeaderDataFavorites"
            :tableStyles="tableStyles"
            :tableData="tableData"
            :hasMore="getFavoritesHasMore"
            :isLoadingMore="tableLoadingMore"
            @loadMore="loadMoreHandler"
            @rowClicked="tableRowClickHandler($event)"/>
            <empty-state-generic
            v-if="!showLoader && !getFavorites.length"
            illustration="emptyState/favorites-folder"
            :title="t('cards.cardlessWithdraw.generateCode.reuseModal.emptyState.title')"
            :description="t('cards.cardlessWithdraw.generateCode.reuseModal.emptyState.description')"/>
            <!-- Detail Error State -->
            <empty-state-error v-if="applyErrorState && !showLoader" :message="t('genericErrorMessage')"/>
    </modal-side-generic>
    <!-- Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import TableGeneric from '@/components/generic/table/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Vue */
import { ref } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Assets & Helpers */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { ACCOUNT_MAIN_CURRENCY } from '@/assets/constants/accounts'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'reuseTransferRowClicked'])

/* mapActions & mapGetters */
const { requestFavorites } = mapActions(moduleMapper.CARDS_CARDLESS_WITHDRAW_GENERATE_CODE)
const { getFavorites, getFavoritesHasMore } = mapGetters(moduleMapper.CARDS_CARDLESS_WITHDRAW_GENERATE_CODE)

const props = defineProps({
    getCardsList: Array
})

/* Data */
const showLoader = ref(false)

const tabsList = ref([
	{
        id: 1,
        label: t('cards.cardlessWithdraw.generateCode.reuseModal.tabsList'),
        selected: true
    }
])

const tableHeaderDataFavorites = ref([
    { label: t('cards.cardlessWithdraw.generateCode.reuseModal.tableHeaderDataFavorites.favoritesName'), dataAttr: 'alias', showInMobile: { groupWith: ['amount', 'destinationMsisdn'] }, rowCellType: 'title' },
	{ label: t('cards.cardlessWithdraw.generateCode.reuseModal.tableHeaderDataFavorites.msisdn'), dataAttr: 'destinationMsisdn', rowCellType: 'description' },
	{ label: t('cards.cardlessWithdraw.generateCode.reuseModal.tableHeaderDataFavorites.originCard'), dataAttr: 'originCard' },
    { label: t('cards.cardlessWithdraw.generateCode.reuseModal.tableHeaderDataFavorites.amount'), dataAttr: 'amount', rowCellType: 'amount' }
])

const tableStyles = [null, null, { width: '120px', 'min-width': '120px' }, { 'text-align': 'end' }]

const tableData = ref(null)
const parsedTableData = () => {
    tableData.value = getFavorites.value.map(favorite => {
        const params = favorite.customerOperationView.parameters
        const amount = getParameterValue(params, 'amount')
        const destinationMsisdn = getParameterValue(params, 'destinationMsisdn')
        return [
            {
                value: favorite.id,
                dataAttr: 'id'
            },
            {
                type: 'title',
                value: favorite.alias,
                dataAttr: 'title'
            },
            {
                type: 'description',
                value: destinationMsisdn,
                dataAttr: 'destinationMsisdn'
            },
            {
                type: 'description',
                value: getCardNumber(getParameterValue(params, 'originCard')),
                dataAttr: 'originCard'
            },
            {
                type: 'amount',
                value: { content: amount, currency: ACCOUNT_MAIN_CURRENCY, noBalanceIcon: true },
                dataAttr: 'amount'
            }
        ]
    })
}

const getParameterValue = (paramsList, paramName) => {
    const targetParam = paramsList.find(param => param.name === paramName)
    return targetParam && targetParam.value ? targetParam.value : '-'
}

const getCardNumber = cardId => {
    return props.getCardsList.find(card => parseInt(card.id) === parseInt(cardId)).cardNumber
}

const tableRowClickHandler = (event) => {
    let data = {}

    const selectedFavorite = getFavorites.value.find(favorite => favorite.id === event.rowId).customerOperationView.parameters

    data = {
        originCard: getParameterValue(selectedFavorite, 'originCard'),
        destinationMsisdn: getParameterValue(selectedFavorite, 'destinationMsisdn'),
        amountCode: getParameterValue(selectedFavorite, 'amountCode'),
        currency: getParameterValue(selectedFavorite, 'currency')
    }
    emit('reuseTransferRowClicked', data)
    emit('hideMe')
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const pageNumber = ref(1)
const tableLoadingMore = ref(false)

const requestFavoritesList = async () => {
	pageNumber.value === 1 ? showLoader.value = true : tableLoadingMore.value = true

    const payload = {
        operationTypes: 19102,
        pageSize: 10,
        pageNumber: pageNumber.value
    }

	try {
        await requestFavorites(payload)
        parsedTableData()
        showLoader.value = false
        tableLoadingMore.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
		showLoader.value = false
		applyErrorState.value = true
		errorHandler(error.response.data, requestFavoritesList)
	}
}

const loadMoreHandler = () => {
    pageNumber.value++
    requestFavoritesList()
}
/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const applyErrorState = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestFavoritesList()
</script>

<style lang="scss" scoped>
</style>
